import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useGenerateShareableLink } from '@/api/endpoints/negotiation/useGenerateShareableLink';
import { useSendInviteEmails } from '@/api/endpoints/negotiation/useSendInviteEmails';
import { Copy02Icon } from '@/assets/icons/Copy02Icon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';

import FormTextField from '@/components/form/FormTextField';

interface PricingTier {
  minHomes: number;
  maxHomes?: number;
  pricePerHome: number;
}

interface ShareGroupModalProps {
  open: boolean;
  onClose: () => void;
  groupTitle: string;
  groupId: number;
  communityId: number;
  groupDescription?: string;
  memberCount?: number;
  metadata?: {
    originalCost?: number;
    savingsAmount?: number;
    estimated_savings?: number;
    pricingTiers?: PricingTier[];
  };
}

interface FormData {
  emails: string;
}

const ShareGroupModal: React.FC<ShareGroupModalProps> = ({
  open,
  onClose,
  groupTitle,
  groupId,
  communityId,
  groupDescription,
  memberCount = 0,
  metadata,
}) => {
  const [, setCopied] = useState(false);
  const [shareableLink, setShareableLink] = useState('');

  const form = useForm<FormData>({
    defaultValues: {
      emails: '',
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
    watch,
  } = form;

  const generateLinkMutation = useGenerateShareableLink({
    onSuccess: data => {
      setShareableLink(data.shareable_link);
    },
    onError: () => {
      enqueueSnackbar('Failed to generate shareable link', { variant: 'error' });
    },
  });

  const sendInvitesMutation = useSendInviteEmails();

  const handleClose = () => {
    reset();
    setCopied(false);
    setShareableLink(''); // Reset link so it's regenerated next time
    onClose();
  };

  // Generate shareable link when modal opens
  useEffect(() => {
    if (open && !shareableLink && !generateLinkMutation.isPending) {
      generateLinkMutation.mutate({
        community_id: communityId,
        negotiation_id: groupId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shareableLink, communityId, groupId]);

  const handleCopyLink = () => {
    // Calculate savings information
    const originalCost = metadata?.originalCost || 0;
    const savingsAmount = metadata?.savingsAmount || metadata?.estimated_savings || 0;
    const savingsPercentage = originalCost > 0 && savingsAmount > 0 
      ? Math.round((savingsAmount / originalCost) * 100) 
      : 0;

    // Create the share message
    let shareMessage = `🏘️ Join our Save Together group: "${groupTitle}"\n\n`;
    
    if (groupDescription) {
      shareMessage += `${groupDescription}\n\n`;
    }

    // Add pricing tiers if available
    if (metadata?.pricingTiers && metadata.pricingTiers.length > 0) {
      shareMessage += '📊 Group Pricing Tiers:\n';
      metadata.pricingTiers.forEach(tier => {
        if (tier.maxHomes) {
          shareMessage += `${tier.minHomes}-${tier.maxHomes} Homes - $${tier.pricePerHome}/home\n`;
        } else {
          shareMessage += `${tier.minHomes}+ Homes - $${tier.pricePerHome}/home\n`;
        }
      });
      shareMessage += '\n';
    } else if (savingsAmount > 0) {
      shareMessage += `💰 Save $${savingsAmount.toFixed(2)} (${savingsPercentage}% off) through group negotiation!\n`;
      if (originalCost > 0) {
        shareMessage += `Original cost: $${originalCost.toFixed(2)} → Group rate: $${(originalCost - savingsAmount).toFixed(2)}\n`;
      }
      shareMessage += '\n';
    }

    if (memberCount > 0) {
      shareMessage += `👥 ${memberCount} ${memberCount === 1 ? 'neighbor has' : 'neighbors have'} already joined\n\n`;
    }

    shareMessage += 'Join us and save together! 🤝\n\n';
    shareMessage += `${shareableLink}`;

    // Copy the full message to clipboard
    navigator.clipboard.writeText(shareMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    enqueueSnackbar('Share message copied to clipboard!', { variant: 'success' });
  };

  // Watch email field for validation
  const emailsValue = watch('emails');

  useEffect(() => {
    if (!emailsValue || !emailsValue.trim()) {
      clearErrors('emails');
      return;
    }

    const emailArray = emailsValue
      .split(',')
      .map(email => email.trim())
      .filter(email => email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalidEmails = emailArray.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setError('emails', {
        type: 'manual',
        message: `Invalid email(s): ${invalidEmails.join(', ')}`,
      });
    } else {
      clearErrors('emails');
    }
  }, [emailsValue, setError, clearErrors]);

  const onSubmit = (data: FormData) => {
    if (!shareableLink) {
      return;
    }

    const emailArray = data.emails
      .split(',')
      .map(email => email.trim())
      .filter(email => email);

    sendInvitesMutation.mutate({
      community_id: communityId,
      negotiation_id: groupId,
      shareable_link: shareableLink,
      emails: emailArray,
    });
  };

  useEffect(() => {
    if (sendInvitesMutation.isSuccess) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendInvitesMutation.isSuccess]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share "{groupTitle}"</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Shareable Link Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Share via link
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Anyone with this link can join the group
            </Typography>
            {generateLinkMutation.isPending ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={56}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              shareableLink && (
                <>
                  {/* Share Message Preview */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      position: 'relative',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Share Message Preview:
                      </Typography>
                      <Tooltip title="Copy share message">
                        <IconButton
                          onClick={handleCopyLink}
                          size="small"
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.50',
                            },
                          }}
                        >
                          <Copy02Icon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        lineHeight: 1.4,
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    >
                      {(() => {
                        const originalCost = metadata?.originalCost || 0;
                        const savingsAmount = 
                        metadata?.savingsAmount || metadata?.estimated_savings || 0;
                        const savingsPercentage = originalCost > 0 && savingsAmount > 0 
                          ? Math.round((savingsAmount / originalCost) * 100) 
                          : 0;

                        let preview = `🏘️ Join our Save Together group: "${groupTitle}"\n\n`;
                      
                        if (groupDescription) {
                          preview += `${groupDescription}\n\n`;
                        }

                        // Add pricing tiers if available
                        if (metadata?.pricingTiers && metadata.pricingTiers.length > 0) {
                          preview += '📊 Group Pricing Tiers:\n';
                          metadata.pricingTiers.forEach(tier => {
                            if (tier.maxHomes) {
                              preview += `${tier.minHomes}-${tier.maxHomes} Homes - $${tier.pricePerHome}/home\n`;
                            } else {
                              preview += `${tier.minHomes}+ Homes - $${tier.pricePerHome}/home\n`;
                            }
                          });
                          preview += '\n';
                        } else if (savingsAmount > 0) {
                          preview += `💰 Save $${savingsAmount.toFixed(2)} (${savingsPercentage}% off) through group negotiation!\n`;
                          if (originalCost > 0) {
                            preview += `Original cost: $${originalCost.toFixed(2)} → Group rate: $${(originalCost - savingsAmount).toFixed(2)}\n`;
                          }
                          preview += '\n';
                        }

                        if (memberCount > 0) {
                          preview += `👥 ${memberCount} ${memberCount === 1 ? 'neighbor has' : 'neighbors have'} already joined\n\n`;
                        }

                        preview += 'Join us and save together! 🤝\n\n';
                        preview += `${shareableLink}`;

                        return preview;
                      })()}
                    </Typography>
                  </Box>
                
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Copy02Icon />}
                    onClick={handleCopyLink}
                    sx={{
                      py: 1.5,
                      bgcolor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                  Copy Share Message
                  </Button>
                </>
              )
            )}
          </Box>

          {/* Email Invites Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Send email invites
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter email addresses separated by commas
            </Typography>
            <FormTextField
              name="emails"
              control={control}
              placeholder="john@example.com, jane@example.com"
              multiline
              rows={3}
              error={!!errors.emails}
              helperText={errors.emails?.message}
              textFieldProps={{
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start' }}>
                      <Mail01Icon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={
            !!errors.emails ||
            !emailsValue?.trim() ||
            sendInvitesMutation.isPending ||
            !shareableLink
          }
          startIcon={
            sendInvitesMutation.isPending ? <CircularProgress size={20} /> : <Mail01Icon />
          }
        >
          {sendInvitesMutation.isPending ? 'Sending...' : 'Send Invites'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareGroupModal;
